import { Tag, TagLabel, TagLeftIcon, Text, Wrap } from "@chakra-ui/react";
import { RiAddLine } from "react-icons/ri";
import IndustryTag from "components/profile/industry-tag";
import KeywordTag from "components/profile/keyword-tag";
import ProfileCard, {
  ProfileCardHeader,
} from "components/profile/profile-card";
import { SkeletonTag, SkeletonTagGroup } from "components/profile/skeleton-tag";
import { useRequestUpdate } from "components/profile/update-modals/request-update-modal";
import usePermission from "hooks/use-permission";
import colorSchemes from "data/color-schemes";
import type { CompanyProfile } from "types/api/company/details/overview";

interface ClassificationCardProps {
  isLoaded: boolean;
  companyProfile: CompanyProfile;
}

const ClassificationCard = ({
  isLoaded,
  companyProfile,
}: ClassificationCardProps) => {
  const { openRequestUpdateModal } = useRequestUpdate();

  const userHasKeywordsPermission = usePermission(
    "companyOverviewIndustryKeywords"
  );

  const getIndustryContent = () => {
    if (!isLoaded) {
      return <SkeletonTag w="33%" />;
    }

    if (!companyProfile?.industry) {
      return <Text fontWeight="bold">No Industry</Text>;
    }

    return (
      <IndustryTag industry={companyProfile?.industry} shouldIncludeLink />
    );
  };

  return (
    <ProfileCard>
      <ProfileCardHeader>Classification</ProfileCardHeader>

      <Text
        as="h3"
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
        fontWeight="medium"
        mb={1}
      >
        Industry
      </Text>

      {getIndustryContent()}

      <Text
        as="h3"
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
        fontWeight="medium"
        mb={1}
        mt={4}
      >
        Keywords
      </Text>
      {isLoaded ? (
        <Wrap spacing={2}>
          {companyProfile?.industryKeywords.map((keyword) => (
            <KeywordTag
              key={keyword.id}
              keyword={keyword.keyword}
              keywordId={keyword.id}
              hasPermission={userHasKeywordsPermission}
              shouldIncludeLink
            />
          ))}
          <Tag
            as="button"
            role="button"
            colorScheme={colorSchemes.keyword}
            size={["sm", "md"]}
            onClick={() => {
              openRequestUpdateModal({
                options: ["overview"],
                message: "Suggest New Keywords:\n",
              });
            }}
          >
            <TagLeftIcon as={RiAddLine} />
            <TagLabel>Suggest new keyword</TagLabel>
          </Tag>
        </Wrap>
      ) : (
        <SkeletonTagGroup noOfLines={3} noOfTags={5} />
      )}
    </ProfileCard>
  );
};

export default ClassificationCard;
